import * as React from 'react'
import { tw } from 'utils/classnames'

// Columns grow 3 → 8 with the viewport, so two-row capacity grows 6 → 16. Each tile beyond a
// breakpoint's two-row capacity stays hidden until that breakpoint kicks in, capping the block at
// two rows. Indices 0–5 are always visible and omitted here.
const TILE_VISIBILITY_BY_INDEX: Record<number, string> = {
  6: 'hidden sm:block',
  7: 'hidden sm:block',
  8: 'hidden md:block',
  9: 'hidden md:block',
  10: 'hidden lg:block',
  11: 'hidden lg:block',
  12: 'hidden xl:block',
  13: 'hidden xl:block',
  14: 'hidden 2xl:block',
  15: 'hidden 2xl:block',
}

export function tileVisibilityClass(index: number): string {
  return TILE_VISIBILITY_BY_INDEX[index] ?? ''
}

function Root({
  id,
  className,
  children,
}: {
  /** Opt-in scroll anchor. Set it when something outside the grid measures where it sits. */
  id?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <section id={id} className={tw('rounded-4 p-6 lg:p-8', className)}>
      {children}
    </section>
  )
}

function Header({
  icon,
  title,
  titleClassName,
  badge,
  subtitle,
  subtitleClassName,
}: {
  icon: React.ReactNode
  title: string
  titleClassName?: string
  badge?: React.ReactNode
  subtitle: string
  subtitleClassName?: string
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className={titleClassName}>{title}</h2>
        {badge}
      </div>
      <p className={tw('mt-1.5', subtitleClassName)}>{subtitle}</p>
    </>
  )
}

function Body({ children }: { children: React.ReactNode }) {
  return <div className="relative mt-5">{children}</div>
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 2xl:grid-cols-8">
      {children}
    </div>
  )
}

function Tile({
  index,
  className,
  children,
}: {
  index: number
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={tw('aspect-[9/16]', tileVisibilityClass(index), className)}>{children}</div>
  )
}

function Cta({ scrimClassName, children }: { scrimClassName?: string; children: React.ReactNode }) {
  return (
    <>
      <div className={tw('pointer-events-none absolute inset-x-0 bottom-0 h-32', scrimClassName)} />
      {children}
    </>
  )
}

export const ShowcaseGrid = { Root, Header, Body, Grid, Tile, Cta }
